/**
 * CandidatesLoadingState — skeleton treatments for the v2 All-Candidates screen.
 *
 * Rendered while the candidates / ranking queries are PENDING (first load or a
 * newly-selected JD) so the page NEVER paints a false-empty "0 candidates"
 * state during the >4s ranking fetch. Mirrors the real Table + Card chrome so
 * the swap to live rows is layout-stable (no reflow flash).
 *
 * `CandidatesTableSkeleton` reuses the exact DataTableV2 `.table-wrap` frame +
 * the vendored Table primitives; `CandidatesCardsSkeleton` mirrors the
 * CandidateCard grid + card frame. Both carry `data-testid="v2-candidates-
 * loading"` so the page test can assert the loading state renders (and
 * "0 candidates" does NOT) while the query is pending.
 *
 * Token discipline: all chrome via semantic utilities (border-border-default,
 * bg-surface-card, surface-subtle via <Skeleton>) — no raw hex / rgb /
 * hsl(var()). Renders in light + dark; honors prefers-reduced-motion via the
 * global animate-pulse degrade.
 */
import type { JSX } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DataTableColumn } from "@/components/v2/patterns/DataTableV2";

// Enough rows/cards to fill the first viewport (matches INITIAL_VISIBLE ≈ 13,
// trimmed to a calmer 8 so the pulse doesn't overwhelm the fold).
const SKELETON_ROW_COUNT = 8;
const SKELETON_CARD_COUNT = 8;

// ── Per-column skeleton shapes — echo each real cell's silhouette so the swap
//    to live data is layout-stable (rank badge, avatar+name, skill pills,
//    fit bar, assessment pills, status select, actions button). ──
function CellSkeleton({ colKey }: { colKey: string }): JSX.Element {
  switch (colKey) {
    case "rank":
      return <Skeleton className="mx-auto h-6 w-6 rounded-md" />;
    case "candidate":
      return (
        <div className="flex items-center gap-[10px]">
          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
          <Skeleton className="h-[13px] w-[120px]" />
        </div>
      );
    case "skills":
      return (
        <div className="flex flex-wrap items-center gap-[5px]">
          <Skeleton className="h-4 w-14 rounded-full" />
          <Skeleton className="h-4 w-12 rounded-full" />
        </div>
      );
    case "fit_score":
      return (
        <div className="flex items-center gap-[10px]">
          <Skeleton className="h-[6px] flex-1 rounded-full" style={{ minWidth: "80px" }} />
          <Skeleton className="h-[13px] w-[22px]" />
        </div>
      );
    case "assessment":
      return (
        <div className="flex flex-wrap items-center gap-[5px]">
          <Skeleton className="h-4 w-20 rounded-full" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
      );
    case "status":
      return <Skeleton className="h-8 w-[140px] rounded-md" />;
    case "actions":
      return <Skeleton className="mx-auto h-6 w-6 rounded-md" />;
    default:
      return <Skeleton className="h-[13px] w-full" />;
  }
}

/** Skeleton for the TABLE view — reuses the DataTableV2 wrap + Table primitives. */
export function CandidatesTableSkeleton({
  columns,
}: {
  columns: DataTableColumn[];
}): JSX.Element {
  return (
    <div
      className="overflow-x-auto rounded-[var(--radius,0.5rem)] border border-border-default bg-surface-card shadow-sm"
      data-testid="v2-candidates-loading"
      aria-busy="true"
    >
      <Table>
        <TableHeader>
          <TableRow className="border-border-default">
            {columns.map((col) => (
              <TableHead
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={col.headClassName}
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
            <TableRow key={i} className="border-border-default hover:bg-transparent">
              {columns.map((col) => (
                <TableCell key={col.key} className={col.cellClassName}>
                  <CellSkeleton colKey={col.key} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Skeleton for the CARD view — mirrors the CandidateCard grid + card frame. */
export function CandidatesCardsSkeleton(): JSX.Element {
  return (
    <div
      data-testid="v2-candidates-loading"
      aria-busy="true"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(262px, 1fr))",
        gap: "16px",
      }}
    >
      {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col rounded-[var(--radius)] border border-border-default bg-surface-card shadow-[var(--shadow-sm)]"
          style={{ borderTop: "3px solid var(--border-default)" }}
        >
          {/* head: rank badge + fit score */}
          <div
            className="flex items-center justify-between"
            style={{ padding: "12px 16px 10px" }}
          >
            <Skeleton className="h-6 w-6 rounded-md" />
            <Skeleton className="h-7 w-8" />
          </div>
          {/* avatar + name + status */}
          <div
            className="flex flex-col items-center"
            style={{ gap: "9px", padding: "4px 16px 14px" }}
          >
            <Skeleton className="h-[72px] w-[72px] rounded-full" />
            <Skeleton className="h-[14px] w-[120px]" />
            <Skeleton className="h-[18px] w-20 rounded-full" />
          </div>
          {/* body: label + skills + why lines */}
          <div
            className="flex flex-1 flex-col border-t border-border-default"
            style={{ gap: "11px", padding: "13px 16px" }}
          >
            <Skeleton className="h-[10px] w-16" />
            <div className="flex flex-wrap" style={{ gap: "5px" }}>
              <Skeleton className="h-4 w-14 rounded-full" />
              <Skeleton className="h-4 w-12 rounded-full" />
            </div>
            <Skeleton className="h-[12px] w-full" />
            <Skeleton className="h-[12px] w-[85%]" />
          </div>
          {/* foot: status control */}
          <div
            className="flex items-center border-t border-border-default"
            style={{ gap: "8px", padding: "9px 16px" }}
          >
            <Skeleton className="h-8 flex-1 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}
